import { Colli } from '@frontend/colli/types';
import { DeviceSensorClient } from '@frontend/device/api';
import { SensorLevel, SensorType } from '@frontend/device/types';
import { Package, PackageType } from '@frontend/package/types';
import { PinCodeType } from '@frontend/pin-code/types';
import { Transaction } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useRef, useState } from 'react';

import { useIdentification, useScanners, useTimers } from '../../../hooks';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { transactionToTransactionExtended, useWizardTransaction } from '../../hooks/useWizardTransaction';
import { DEFAULT_PIN_LENGTH, getPinCodeLength } from '../../utils/pinCodeCache';
import { resolveDropOffColliContext, resolveDropOffPackageContext } from '../../utils/seed-drop-off-colli-context';
import { useScreen } from '../useScreen';

interface ViewProps {
    pinCode: string[];
    pinLength: number;
    isLoadingPinLength: boolean;
    onNumberPress: (num: number) => void;
    onBackspace: () => void;
    onSubmit: () => void;
    onBack: (screenType: ScreenType) => void;
    canSubmit: boolean;
    isLocked: boolean;
    showModal: boolean;
    remainingTime: number;
    hasError: boolean;
    closeModal: () => void;

    resolving: boolean;
    stopResolving: () => void;
}

const MAX_ATTEMPTS = 4;
const LOCKOUT_DURATION = 60; // seconds
const SENSOR_TIMEOUT = 10000; // 10 seconds in milliseconds
const AUTO_SUBMIT_DELAY = 1000;
const TIMER_ID = 'drop-off-pin-code-lockout';

const useDropOffPinCode = (): ViewProps => {
    const { navigationState, store, onBack, onSubmit, updateWizardData } = useScreen();
    const wizardTransaction = useWizardTransaction({
        wizardId: store!.wizard.id,
        store,
        autoFetch: false,
        autoCreate: false
    });
    const { localUpdatePackage } = useWizardPackage({
        wizardId: store!.wizard.id,
        store,
        autoFetch: false,
        autoCreate: false
    });
    const [pinCode, setPinCode] = useState<string[]>([]);
    const [pinLength, setPinLength] = useState<number>(DEFAULT_PIN_LENGTH);
    const [isLoadingPinLength, setIsLoadingPinLength] = useState<boolean>(true);
    const [attemptCount, setAttemptCount] = useState<number>(0);
    const [hasError, setHasError] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [resolving, setResolving] = useState<boolean>(false);
    const sensorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const autoSubmitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleSubmitRef = useRef<() => void>(() => undefined);

    // Use persistent timer hook with callback to reset attempts when timer completes
    const {
        remainingTime,
        isActive: isLocked,
        startTimer
    } = useTimers(TIMER_ID, () => {
        // Reset attempt count when timer completes
        setAttemptCount(0);
        setShowModal(false);
        setPinCode([]);
        setHasError(false);
    });

    // Automatically show/hide modal based on timer state
    useEffect(() => {
        if (isLocked) {
            setShowModal(true);
        } else {
            setShowModal(false);
        }
    }, [isLocked]);

    const onNumberPress = (number: number) => {
        if (pinCode.length < pinLength && !isLocked) {
            setPinCode([...pinCode, number.toString()]);
            setHasError(false);
        }
    };

    const onBackspace = () => {
        if (pinCode.length > 0 && !isLocked) {
            setPinCode(pinCode.slice(0, -1));
            setHasError(false);
        }
    };

    useScanners({
        id: 'DROPOFF_PIN_CODE_SCANNER',
        onDeviceSensorReceived: (code: string) => {
            setResolving(true);
            sensorTimeoutRef.current = setTimeout(() => {
                // No response received within 10 seconds
                onFail();
                sensorTimeoutRef.current = null;
            }, SENSOR_TIMEOUT);
        }
    });
    useIdentification({
        id: 'DROPOFF_PIN_CODE_IDENTIFICATION',
        onNoMatchingIdentificationFound: () => {
            onFail();
        },
        onUnlinkedIdentificationFound: () => {
            onFail();
        },
        onPackageIdentified: async (pkg: Package) => {
            if (pkg.type !== PackageType.DROP_OFF) {
                onFail();
                return;
            }

            if (sensorTimeoutRef.current) {
                clearTimeout(sensorTimeoutRef.current);
                sensorTimeoutRef.current = null;
            }

            if (!pkg.transaction_id || !navigationState.iot?.account_id) {
                onFail();
                return;
            }

            try {
                const { transaction, collis, packages } = await resolveDropOffPackageContext({
                    accountId: navigationState.iot.account_id,
                    spotId: navigationState.spot?.id ?? navigationState.iot.spot_id,
                    package: pkg
                });

                await wizardTransaction.localUpdateTransaction(transaction);
                updateWizardData({ collis, packages });
                if (packages[0]) {
                    await localUpdatePackage(packages[0]);
                } else {
                    await localUpdatePackage(pkg);
                }

                setHasError(false);
                setResolving(false);
                const nextStep = store?.steps.find((step) => step.screen === ScreenType.DROP_OFF_PIN_CODE && step.trigger === ScreenTrigger.SUBMIT);
                onSubmit(nextStep);
            } catch (error) {
                console.error('Failed to resolve package drop-off context:', error);
                onFail();
            }
        },
        onColliIdentified: async (colli: Colli) => {
            if (sensorTimeoutRef.current) {
                clearTimeout(sensorTimeoutRef.current);
                sensorTimeoutRef.current = null;
            }

            if (!colli.transaction_id || !navigationState.iot?.account_id) {
                onFail();
                return;
            }

            try {
                const { transaction, collis, packages } = await resolveDropOffColliContext({
                    accountId: navigationState.iot.account_id,
                    spotId: navigationState.spot?.id ?? navigationState.iot.spot_id,
                    colli
                });

                await wizardTransaction.localUpdateTransaction(transaction);
                updateWizardData({ collis, packages });
                if (packages[0]) {
                    await localUpdatePackage(packages[0]);
                }

                setHasError(false);
                setResolving(false);
                const nextStep = store?.steps.find((step) => step.screen === ScreenType.DROP_OFF_PIN_CODE && step.trigger === ScreenTrigger.SUBMIT);
                onSubmit(nextStep);
            } catch (error) {
                console.error('Failed to resolve colli drop-off context:', error);
                onFail();
            }
        },
        onTransactionIdentified: (transaction: Transaction) => {
            if (sensorTimeoutRef.current) {
                clearTimeout(sensorTimeoutRef.current);
                sensorTimeoutRef.current = null;
            }

            setHasError(false);
            wizardTransaction.localUpdateTransaction(transactionToTransactionExtended(transaction));
            setResolving(false);
            const nextStep = store?.steps.find((step) => step.screen === ScreenType.DROP_OFF_PIN_CODE && step.trigger === ScreenTrigger.SUBMIT);
            onSubmit(nextStep);
        }
    });

    const handleSubmit = async () => {
        if (pinCode.length === pinLength && !isLocked) {
            const pinCodeStr = pinCode.join('');

            if (navigationState.iot && navigationState.device) {
                setResolving(true);
                // Clear any existing timeout
                if (sensorTimeoutRef.current) {
                    clearTimeout(sensorTimeoutRef.current);
                }
                updateWizardData({ package_type: PackageType.DROP_OFF });
                DeviceSensorClient.postDeviceSensor(navigationState.iot.account_id, navigationState.iot.id, navigationState.device.id, {
                    level: SensorLevel.INFO,
                    data: {
                        input: pinCodeStr,
                        pin_code_type: PinCodeType.DROP_OFF
                    },
                    type: SensorType.PIN_CODE
                })
                    .then(() => {
                        // Set timeout for sensor response only after API call succeeds
                        sensorTimeoutRef.current = setTimeout(() => {
                            // No response received within 10 seconds
                            onFail();
                            sensorTimeoutRef.current = null;
                        }, SENSOR_TIMEOUT);
                    })
                    .catch((error) => {
                        // If the API call fails, show error immediately
                        onFail();
                    });
            }
        }
    };

    handleSubmitRef.current = () => {
        void handleSubmit();
    };

    const onFail = () => {
        setHasError(true);
        setResolving(false);
        const newAttemptCount = attemptCount + 1;
        setAttemptCount(newAttemptCount);
        if (newAttemptCount >= MAX_ATTEMPTS) {
            setShowModal(true);
            startTimer(LOCKOUT_DURATION);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setPinCode([]);
        setHasError(false);
        // Don't reset timer or attempt count - they persist across navigation
    };

    const canSubmit = pinCode.length === pinLength && !isLocked;

    useEffect(() => {
        if (navigationState.iot?.account_id) {
            getPinCodeLength(navigationState.iot.account_id)
                .then((length) => {
                    setPinLength(length);
                })
                .finally(() => {
                    setIsLoadingPinLength(false);
                });
        }
    }, [navigationState.iot?.account_id]);

    useEffect(() => {
        if (autoSubmitTimeoutRef.current) {
            clearTimeout(autoSubmitTimeoutRef.current);
            autoSubmitTimeoutRef.current = null;
        }

        if (pinCode.length === pinLength && !isLocked && !resolving && !hasError) {
            autoSubmitTimeoutRef.current = setTimeout(() => {
                autoSubmitTimeoutRef.current = null;
                handleSubmitRef.current();
            }, AUTO_SUBMIT_DELAY);
        }

        return () => {
            if (autoSubmitTimeoutRef.current) {
                clearTimeout(autoSubmitTimeoutRef.current);
                autoSubmitTimeoutRef.current = null;
            }
        };
    }, [pinCode, pinLength, isLocked, resolving, hasError]);

    useEffect(() => {
        return () => {
            // Clear timeout on unmount
            if (sensorTimeoutRef.current) {
                clearTimeout(sensorTimeoutRef.current);
                sensorTimeoutRef.current = null;
            }
            if (autoSubmitTimeoutRef.current) {
                clearTimeout(autoSubmitTimeoutRef.current);
                autoSubmitTimeoutRef.current = null;
            }
        };
    }, []);

    return {
        pinCode,
        pinLength,
        isLoadingPinLength,
        onNumberPress,
        onBackspace,
        onSubmit: handleSubmit,
        onBack,
        canSubmit,
        isLocked,
        showModal,
        remainingTime,
        hasError,
        closeModal,

        resolving,
        stopResolving: () => {
            setResolving(false);
            if (sensorTimeoutRef.current) {
                clearTimeout(sensorTimeoutRef.current);
                sensorTimeoutRef.current = null;
            }
        }
    };
};

export default useDropOffPinCode;
